// Health copy (UserFlows §4.2, C-12, S-07; Observability §5.6; API.md §6.16). UserFlows owns the copy; where it is
// silent the task file's assumptions apply (Observability §5.6's card lines, "Degraded", "Not connected").
import type { ClinicHealthStatus, DayLevel, HealthComponent, HealthState } from '@pa/api/clinic'

/** UserFlows C-12's purpose line (kept as UserFlows has it). */
export const C12_PURPOSE =
  'PF Keeper health, logged from its heartbeat every 60 seconds. Hover a day to see what happened.'

export const HISTORY_DAYS = 90
export const NARROW_DAYS = 30

export const COMPONENTS: readonly { id: HealthComponent; name: string; desc: string }[] = [
  {
    id: 'pf_keeper_app',
    name: 'PF Keeper app',
    desc: 'The PF Keeper app is running and sending heartbeats to the scheduler server.',
  },
  {
    id: 'pf_session',
    name: 'Practice Fusion session',
    desc: 'PF Keeper is logged in to Practice Fusion and can read and write appointments.',
  },
]

export const componentName = (id: HealthComponent): string => COMPONENTS.find((c) => c.id === id)?.name ?? id

/** The legend and the bars' accessible names (UserFlows C-12; grey "No data" for days without data). */
export const LEVEL_LABEL: Record<DayLevel, string> = {
  ok: 'No downtime',
  minor: 'Minor outage',
  major: 'Major outage',
  no_data: 'No data',
}

/** The mockup's bar classes (`lv-none` is its grey "no data"). */
export const levelClass = (level: DayLevel): string => (level === 'no_data' ? 'lv-none' : `lv-${level}`)

/** C-12 component state (API.md §6.16): Operational / Degraded / Down / No data. */
export const STATE_LABEL: Record<HealthState, string> = {
  up: 'Operational',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'No data',
}

/** The C-12 banner's headline (API.md §6.16). */
export const STATUS_HEADLINE: Record<ClinicHealthStatus, string> = {
  healthy: 'All systems operational',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'No data',
  not_connected: 'Not connected',
  inactive: 'Inactive',
}

/** S-07's state words for the keeper and session columns. */
export const SHORT_STATE: Record<HealthState, string> = {
  up: 'Operational',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'No data',
}

/** S-07 / S-02 clinic status pill. */
export const STATUS_LABEL: Record<ClinicHealthStatus, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'No data',
  not_connected: 'Not connected',
  inactive: 'Inactive',
}

/**
 * Task 25 (D-82): the API's `pf.degradedReason` while PF Keeper waits on PF's security code page (Observability §5.6
 * amber). The PF status card and S-02 show it as their own line.
 */
export const AWAITING_CODE_REASON = 'PF waiting for a security code'

/** True when the clinic is amber because PF Keeper waits for a PF security code. */
export const isAwaitingCode = (pf: { status: ClinicHealthStatus; degradedReason: string | null }): boolean =>
  pf.status === 'degraded' && pf.degradedReason === AWAITING_CODE_REASON
